import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  CountrySelect,
  InputSelect,
} from '@features/ui';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOption } from '@src/types';
import { validZipCode } from '@lib/onboarding.ts';
import { ZipRange } from '@src/services/dropdown.ts';
import { cn } from '@lib/utils.ts';
import { PubicTermAndConditionModal } from '@pages/user/PublicTermAndConditionModal';

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Email is invalid').required('Email is required'),
  password: yup
    .string()
    .matches(/^(?=.*[a-z])/, 'Must contain at least one lowercase letter')
    .matches(/^(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
    .matches(/^(?=.*\d)/, 'Must contain at least one number')
    .matches(
      /^(?=.*[@$!%*?&^#()\-+=_{}[\]:;<>.,/\\|`~])/,
      'Must contain at least one special character'
    )
    .matches(/^[^\s]+$/, 'Must not contain spaces')
    .min(8, 'Password must be at least 8 characters long')
    .max(16, 'Password must be at most 16 characters long')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .test('passwords-match', 'Passwords must match', function (value) {
      return this.parent.password === value;
    }),
  phoneNumber: yup.string().required('Phone number is required'),
  street: yup.string().required('Street is required'),
  city: yup.string().required('City is required'),
  country: yup.string().required('Country is required'),
  state: yup.string().when('country', {
    is: (val: string) => val === 'US',
    then: (schema) => schema.required('State is required'),
    otherwise: (schema) => schema.optional(),
  }),
  zip_code: yup
    .string()
    .when('country', {
      is: (val: string) => val === 'US',
      then: (schema) => schema.required('Zip code is required'),
      otherwise: (schema) => schema.optional(),
    })
    .test(
      'zip-valid',
      'Zip code is not eligible for the selected country',
      function (value) {
        const { zip_range, country } = this.parent;

        if (country !== 'US') return true;
        if (!value || !Array.isArray(zip_range)) return true;

        return validZipCode(value, zip_range as ZipRange[]);
      }
    ),
  zip_range: yup.array(),
});

export type SignUpFormData = yup.InferType<typeof schema>;

type Props = {
  className?: string;
  countryOptions: TOption[];
  states: TOption[];
  onSubmit: (data: SignUpFormData) => void;
};

export const SignUpForm: React.FC<Props> = ({
  className,
  onSubmit,
  states,
  countryOptions,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [agreementChecked, setAgreementChecked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    resetField,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      country: '',
    },
  });

  const country = watch('country');

  const handleChangeState = useCallback(
    async (
      field: ControllerRenderProps<SignUpFormData, 'state'>,
      value: string
    ) => {
      field.onChange(value);
      const zipRange = states.find((item) => item.value === value)?.zipRange;

      setValue('zip_range', zipRange);
    },
    [setValue, states]
  );

  return (
    <form
      autoComplete="off"
      className="w-full lg:max-w-[584px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Card className={`py-8 px-8 xl:py-12 xl:px-20 ${className ?? ''}`}>
        <h3 className="text-center text-[27px] mb-7 font-medium">
          {t('signup.form.started')}
        </h3>
        <div className="flex flex-col gap-y-5">
          <div className="flex flex-col gap-y-5 xl:gap-y-0 xl:flex-row gap-x-5">
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => (
                <div className="flex w-full flex-col gap-y-2.5">
                  <Label htmlFor={field.name}>
                    {t('common.form.firstName')}
                  </Label>
                  <div>
                    <Input
                      {...field}
                      error={!!errors.firstName}
                      id={field.name}
                      inputSize={'lg'}
                      placeholder={t('common.form.firstNamePlaceholder')}
                    />
                    {errors.firstName && (
                      <p className="text-destructive text-xs mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="lastName"
              render={({ field }) => (
                <div className="flex w-full flex-col gap-y-2.5">
                  <Label htmlFor={field.name}>
                    {t('common.form.lastName')}
                  </Label>
                  <div>
                    <Input
                      {...field}
                      error={!!errors.lastName}
                      id={field.name}
                      inputSize={'lg'}
                      placeholder={t('common.form.lastNamePlaceholder')}
                    />
                    {errors.lastName && (
                      <p className="text-destructive text-xs mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>{t('common.form.email')}</Label>
                <div>
                  <Input
                    {...field}
                    error={!!errors.email}
                    id={field.name}
                    inputSize={'lg'}
                    placeholder={t('common.form.email')}
                  />
                  {errors.email && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>{t('common.form.password')}</Label>
                <div>
                  <Input
                    {...field}
                    error={!!errors.password}
                    id={field.name}
                    inputSize={'lg'}
                    placeholder={t('common.form.password')}
                    type="password"
                  />
                  {errors.password && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="confirmPassword"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>
                  {t('common.form.confirmPassword')}
                </Label>
                <div>
                  <Input
                    {...field}
                    error={!!errors.confirmPassword}
                    id={field.name}
                    inputSize={'lg'}
                    placeholder={t('common.form.confirmPassword')}
                    type="password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>
                  {t('common.form.phoneNumber')}
                </Label>
                <div>
                  <Input
                    {...field}
                    error={!!errors.phoneNumber}
                    id={field.name}
                    inputSize={'lg'}
                    placeholder={t('common.form.phoneNumber')}
                  />
                  {errors.phoneNumber && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="street"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>{t('checkout.form.street')}</Label>
                <div>
                  <Input
                    id="street"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.streetPlaceholder')}
                    textSize={'sm'}
                    {...field}
                  />
                  {errors.street && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.street.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="city"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>{t('checkout.form.city')}</Label>
                <div>
                  <Input
                    id="city"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.cityPlaceholder')}
                    textSize={'sm'}
                    {...field}
                  />
                  {errors.city && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.city.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor={field.name}>{t('common.form.country')}</Label>
                <div>
                  <CountrySelect
                    countryOptions={countryOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      resetField('state');
                      resetField('zip_code');
                      resetField('zip_range');
                    }}
                  />
                  {errors.country && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.country.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="state"
            render={({ field }) => (
              <div
                className={cn(
                  'flex-col gap-y-2.5',
                  country !== 'US' ? 'hidden' : 'flex'
                )}
              >
                <Label htmlFor={field.name}>{t('checkout.form.state')}</Label>
                <div>
                  <InputSelect
                    borderColor={'border-[#979797]'}
                    className={'rounded-[10px] h-[42px] text-sm'}
                    options={states}
                    placeholder={t('checkout.form.statePlaceholder')}
                    value={field.value ?? ''}
                    onChange={(value) => handleChangeState(field, value)}
                  />
                  {errors.state && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.state.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="zip_code"
            render={({ field }) => (
              <div
                className={cn(
                  'flex-col gap-y-2.5',
                  country !== 'US' ? 'hidden' : 'flex'
                )}
              >
                <Label htmlFor={field.name}>{t('checkout.form.zipCode')}</Label>
                <div>
                  <Input
                    {...field}
                    id="zip_code"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.zipCodePlaceholder')}
                    textSize={'sm'}
                    value={field.value ?? ''}
                  />
                  {errors.zip_code && (
                    <p className="text-destructive text-xs mt-1">
                      {errors.zip_code.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
        </div>
        <div className="flex items-center justify-center gap-x-2 mt-5 mb-7">
          <Checkbox
            id="agree_term_policy"
            onCheckedChange={(checked) =>
              setAgreementChecked(checked as boolean)
            }
          />
          <Label className={'font-medium'} htmlFor="agree_term_policy">
            <span className="mr-1">{t('signup.agreement')}</span>
            <span
              className="underline cursor-pointer hover:text-accent-foreground"
              role="button"
              tabIndex={0}
              onClick={() => setShowTermsModal(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setShowTermsModal(true);
              }}
            >
              {t('signup.termPolicy')}
            </span>
          </Label>
        </div>
        <PubicTermAndConditionModal
          open={showTermsModal}
          onClose={() => setShowTermsModal(false)}
        />
        <Button
          className="w-full font-bold mb-5 text-base"
          disabled={!agreementChecked}
          loading={isSubmitting}
          size="lg"
          type="submit"
          variant="gradient"
        >
          {t('common.form.signUpBtn')}
        </Button>
        <p className="text-sm text-center font-[Poppins] font-medium">
          {t('signin.form.alreadyHaveAccount')}{' '}
          <span
            className="underline cursor-pointer hover:text-accent-foreground"
            onClick={() => navigate('/signin')}
          >
            {t('signin.form.signIn')}
          </span>
        </p>
      </Card>
    </form>
  );
};
